// Dominio del modo "Variantes de acabado" — el MISMO mueble, una imagen por material o
// color, sólo para marcas del rubro Hogar y Decoración
// (ADR-2026-09-03-modo-variantes-de-acabado).
//
// Vive aparte de la pantalla (`modes/VariantesAcabadoMode.tsx`) por lo mismo que
// `mostrarComoFunciona.ts` vive aparte de la suya: son las listas que decide producto
// —qué acabados se ofrecen, en qué categorías, con qué fondo— y cambiarlas no
// debería obligar a abrir el componente. Cuando exista backend, los `id` de acá son los
// que van a viajar en el payload.
//
// POR QUÉ ES UN MODO Y NO UNA VISTA DEL CATÁLOGO: en el Catálogo la cantidad de imágenes
// es "producto × vista". Acá es "foto de partida × acabado", y las fotos no son productos
// del catálogo sino creaciones o archivos del usuario. Ese cálculo no entra en el del
// Catálogo, así que va aparte.

import { creditosPorResolucion, type ResolucionImagen } from '../../lib/resolucionImagen';
import type { FormatoVariantes } from '../../lib/variantesAcabado';

/**
 * Las solapas del panel: los EJES por los que varía un mueble.
 *
 * No son "tipos de material" sino las cuatro decisiones que de verdad cambian una foto de
 * catálogo de Hogar y Decoración (ADR-2026-09-23-los-ejes-de-variantes-son-cuatro): el
 * color, la textura del tapizado, la madera/estructura y la terminación de superficie.
 * Antes eran maderas / telas / metales / colores, que es como se ordena un MUESTRARIO y no
 * como se mira un mueble: un sofá no se compara "por metales", se compara por su color y
 * por su tela.
 *
 * `mis-muestras` es lo que sube el usuario.
 */
export type CategoriaAcabadoId =
  | 'color'
  | 'tapizado'
  | 'madera'
  | 'superficie'
  | 'mis-muestras';

export interface CategoriaAcabado {
  id: CategoriaAcabadoId;
  /** El nombre de la pastilla y del encabezado de la sección. */
  nombre: string;
  /** En minúscula, para la bajada de la card ("4 elegidos · colores y maderas"). */
  nombreCorto: string;
}

/**
 * El ORDEN es el del panel: pastillas y secciones salen de esta lista, así que moverla
 * mueve las dos. "Mis muestras" va PRIMERA —y no al final, donde estaba— porque es la
 * única que el usuario llena, y al final quedaba a un scroll de distancia detrás de las
 * muestras curadas (ADR-2026-09-04-mis-swatches-abre-el-panel-de-acabados).
 */
export const CATEGORIAS_ACABADO: CategoriaAcabado[] = [
  { id: 'mis-muestras', nombre: 'Mis muestras', nombreCorto: 'tus muestras' },
  { id: 'color', nombre: 'Color', nombreCorto: 'colores' },
  { id: 'tapizado', nombre: 'Tapizado', nombreCorto: 'tapizados' },
  { id: 'madera', nombre: 'Madera', nombreCorto: 'maderas' },
  { id: 'superficie', nombre: 'Terminación', nombreCorto: 'terminaciones' },
];

export interface Acabado {
  id: string;
  nombre: string;
  categoria: CategoriaAcabadoId;
  /**
   * El color PLANO del acabado. Es lo que dibujan el mosaico de la card, las pastillas
   * de "La tanda sale así" y el pie de cada resultado.
   *
   * La muestra grande del panel le agrega encima el relieve y la trama que le toca
   * (la suya, si tiene; si no, la de su categoría) en vez de traer un gradiente propio.
   * Un acabado con foto real —los que sube el usuario— trae `imageUrl` y no usa nada de
   * esto.
   */
  color: string;
  /**
   * La trama de ESTE acabado, cuando la de su categoría no alcanza. Los ejes mezclan
   * materiales —en "Terminación de superficie" conviven un laqueado liso, una piedra y
   * una esterilla tejida—, así que la trama dejó de ser propiedad SÓLO de la categoría
   * (ADR-2026-09-23-los-ejes-de-variantes-son-cuatro). Sin esto, la de su categoría.
   */
  trama?: string;
  /** Foto del muestra, para los que subió el usuario (`objectURL`) o los que traiga el backend. */
  imageUrl?: string;
}

/**
 * Las tramas: CSS que se superpone al color plano para que una muestra se lea como
 * material. Son CSS y no fotos a propósito: son muestras de 90px que sólo tienen que
 * decir "madera veteada" / "tela" / "tejido", y una foto por acabado son assets que
 * todavía no existen. Cuando lleguen las muestras reales, entran por `imageUrl`.
 */
const TRAMA_MADERA =
  'repeating-linear-gradient(97deg, rgba(0,0,0,0.08) 0 2px, rgba(255,255,255,0.06) 2px 7px)';
const TRAMA_TELA =
  'repeating-linear-gradient(45deg, rgba(255,255,255,0.14) 0 3px, rgba(0,0,0,0.06) 3px 6px)';
/** Terciopelo: el pelo en una dirección, que es de dónde le salen el brillo y la profundidad. */
const TRAMA_TERCIOPELO =
  'linear-gradient(100deg, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 40%, rgba(0,0,0,0.20) 100%), repeating-linear-gradient(90deg, rgba(255,255,255,0.07) 0 3px, rgba(0,0,0,0.07) 3px 7px)';
/** Bouclé: los bucles, en dos puntos desfasados para que no se lea como lunares. */
const TRAMA_BOUCLE =
  'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.13) 1.5px, transparent 1.7px) 0 0 / 7px 7px, radial-gradient(circle at 3px 3px, rgba(255,255,255,0.50) 1.2px, transparent 1.4px) 2px 3px / 7px 7px';
/** Esterilla / ratán: el tejido cruzado. */
const TRAMA_TEJIDO =
  'repeating-linear-gradient(45deg, rgba(0,0,0,0.13) 0 3px, transparent 3px 7px), repeating-linear-gradient(-45deg, rgba(255,255,255,0.28) 0 3px, transparent 3px 7px)';
/** Travertino: las vetas, casi horizontales y de ancho desparejo. */
const TRAMA_PIEDRA =
  'repeating-linear-gradient(6deg, rgba(255,255,255,0.22) 0 2px, rgba(0,0,0,0.05) 2px 5px, rgba(255,255,255,0.10) 5px 12px)';

/**
 * La trama POR DEFECTO de cada eje — la que se usa cuando el acabado no trae la suya.
 * Hay eje que no tiene una sola: en "Terminación de superficie" no existe la trama que
 * sirva a la vez para un laqueado liso, una piedra y una esterilla, así que ahí la pone
 * cada acabado.
 */
export const TRAMA_POR_CATEGORIA: Record<CategoriaAcabadoId, string | undefined> = {
  // El color es color: plano, con el relieve que le agrega la pantalla y nada más.
  color: undefined,
  tapizado: TRAMA_TELA,
  madera: TRAMA_MADERA,
  superficie: undefined,
  'mis-muestras': undefined,
};

/**
 * La paleta CURADA: TRES acabados por eje, ni uno más
 * (ADR-2026-09-23-los-ejes-de-variantes-son-cuatro).
 *
 * No es el muestrario de una fábrica: son los acabados que MUEVEN la foto contra lo que
 * los usuarios ya tienen cargado. Por eso el gris queda afuera del eje de color —es el
 * color de base de la mayoría de los sofás del catálogo, así que generarlo es pagar por
 * la foto que ya se tiene—; por eso están el bouclé y el nogal, que casi no existen en el
 * catálogo y son lo más pedido en deco hoy; y por eso están al lado el terciopelo y el
 * roble natural, que son lo que ya abunda y por lo tanto la VUELTA al original. Elegir de
 * una lista corta es lo que hace que el modo se resuelva en dos toques; el acabado exacto
 * que no está se sube como muestra (categoría `mis-muestras`).
 */
export const ACABADOS: Acabado[] = [
  // ── Color ────────────────────────────────────────────────────────────────
  // El neutro cálido: la alternativa al gris sin salirse de lo seguro.
  { id: 'arena-crema', nombre: 'Arena / crema', categoria: 'color', color: '#E1D4BF' },
  { id: 'terracota', nombre: 'Terracota', categoria: 'color', color: '#B5603F' },
  // El profundo, el que más luce en terciopelo y en laqueado.
  { id: 'verde-botella', nombre: 'Verde botella', categoria: 'color', color: '#22493A' },

  // ── Tapizado ─────────────────────────────────────────────────────────────
  // El color de los tres es NEUTRO a propósito: acá el eje es la TEXTURA y el tono lo
  // decide el eje de color. Lo que los separa en la grilla es la trama, no el color.
  {
    id: 'terciopelo',
    nombre: 'Terciopelo',
    categoria: 'tapizado',
    color: '#8A7A72',
    trama: TRAMA_TERCIOPELO,
  },
  {
    id: 'boucle',
    nombre: 'Bouclé',
    categoria: 'tapizado',
    color: '#E6E1D7',
    trama: TRAMA_BOUCLE,
  },
  // El look natural, la contracara del terciopelo. Se queda con la trama del eje.
  { id: 'lino-rustico', nombre: 'Lino rústico', categoria: 'tapizado', color: '#D8CFBE' },

  // ── Madera y estructura ──────────────────────────────────────────────────
  // El contraste directo contra el roble que ya tienen.
  { id: 'nogal', nombre: 'Nogal oscuro', categoria: 'madera', color: '#5A3B28' },
  { id: 'roble-natural', nombre: 'Roble natural', categoria: 'madera', color: '#C6A276' },
  // Madera teñida o metal: el look industrial. Con la veta del eje, que a este tono
  // queda como un teñido y no como un roble.
  { id: 'negro-mate', nombre: 'Negro mate', categoria: 'madera', color: '#2A2A2C' },

  // ── Terminación de superficie ────────────────────────────────────────────
  // Blanco o de color: se combina con el eje de color. Liso, sin trama.
  { id: 'laqueado-mate', nombre: 'Laqueado mate', categoria: 'superficie', color: '#EDEAE4' },
  // Para tapas de mesa y ratonas.
  {
    id: 'simil-piedra',
    nombre: 'Símil piedra',
    categoria: 'superficie',
    color: '#CFC0A8',
    trama: TRAMA_PIEDRA,
  },
  // Para frentes de aparadores y racks, y respaldos de silla.
  {
    id: 'esterilla-ratan',
    nombre: 'Esterilla / ratán',
    categoria: 'superficie',
    color: '#C9A46A',
    trama: TRAMA_TEJIDO,
  },
];

export const acabadoPorId = (id: string): Acabado | undefined =>
  ACABADOS.find((a) => a.id === id);

/**
 * Lo que viene elegido al abrir el modo: NADA
 * (ADR-2026-09-04-la-paleta-arranca-vacia).
 *
 * Arrancaba con ocho —tres maderas y cinco colores, la tanda del diseño— para que la
 * pantalla no se viera muda y el costo estuviera a la vista antes de tocar nada. El
 * problema es que esos ocho se cobran: quien no revisa la paleta genera —y paga— una
 * tanda que no eligió, y quien sí la revisa arranca DESELECCIONANDO. La lista queda
 * porque el estado inicial sale de acá; volver a poner una tanda de arranque es llenarla.
 */
export const ACABADOS_POR_DEFECTO: string[] = [];

/**
 * DÓNDE se ve el producto: el fondo de la imagen.
 *
 * Antes esto se llamaba "encuadre y fondo" y decidía las dos cosas a la vez. El encuadre
 * se fue (ADR-2026-09-04-se-va-el-encuadre-y-el-fondo-va-por-imagen): las tres opciones
 * siempre hablaron del FONDO —el de tu foto, uno de estudio, un ambiente— y prometer que
 * además se reencuadra era prometer de más. Es de CADA foto de partida y no de la tanda.
 */
export type FondoId = 'original' | 'estudio' | 'ambiente' | 'describir';

export interface Fondo {
  id: FondoId;
  nombre: string;
  /** El valor que muestra la card de la columna. */
  valor: string;
  descripcion: string;
}

export const FONDOS: Fondo[] = [
  {
    id: 'original',
    nombre: 'El de la foto',
    valor: 'El de la foto',
    descripcion:
      'Se mantienen la luz y el fondo de tu foto. Lo único que cambia es el acabado.',
  },
  {
    id: 'estudio',
    nombre: 'De estudio',
    valor: 'Fondo de estudio',
    descripcion: 'Fondo neutro parejo y sombra suave.',
  },
  {
    id: 'ambiente',
    nombre: 'Un ambiente',
    valor: 'En un ambiente',
    descripcion: 'El mueble montado en una escena, la misma para todos sus acabados.',
  },
  {
    id: 'describir',
    // La cuarta no es otro TIPO de fondo: es la salida cuando ninguno de los tres alcanza
    // (ADR-2026-09-04-describir-fondo-es-la-cuarta-opcion). Elegirla despliega el campo
    // de texto; las otras tres no lo muestran.
    nombre: 'Describir fondo',
    valor: 'Fondo a medida',
    descripcion: 'Escribí el fondo que querés y se arma con eso.',
  },
];

export const fondoPorId = (id: FondoId): Fondo =>
  FONDOS.find((f) => f.id === id) ?? FONDOS[0];

/**
 * Créditos por imagen en 1K. Igual que el resto de los modos de imagen
 * (`creditosPorUnidad: 1`, `unidadConsumo: 'imagen'` en `modesCatalog`); en 2K y 4K el
 * backend multiplica (×2 en 4K) y acá se muestra lo mismo que se va a cobrar.
 */
export const CREDITOS_POR_IMAGEN = 1;

/**
 * Una imagen por acabado y por foto de partida: la cuenta del modo entero, en una línea.
 * El parámetro es el TOTAL ya multiplicado (`fotos × acabados`) y no los acabados solos,
 * desde que la imagen de partida puede ser más de una
 * (ADR-2026-09-04-la-imagen-de-partida-puede-ser-mas-de-una). La calidad multiplica: es
 * lo que el gate del backend verifica (`creditos_estimados`), así que el pill tiene que
 * decir ese número y no el de 1K.
 */
export const costoTanda = (cantidadImagenes: number, calidad?: ResolucionImagen): number =>
  cantidadImagenes * CREDITOS_POR_IMAGEN * creditosPorResolucion(calidad);

/**
 * Proporción ancho/alto de cada formato, para dibujar los recuadros de la tanda con la
 * forma de lo que va a salir. Las claves son los `id` de `FORMAT_OPTIONS_V2`.
 * Misma tabla que `mostrarComoFunciona.ts`: se duplica a propósito para no acoplar dos
 * modos que no comparten nada más (y que la del video se pueda tocar sin tocar ésta).
 */
export const RATIO_POR_FORMATO: Record<string, number> = {
  post: 4 / 5,
  story: 9 / 16,
  square: 1,
  portrait: 2 / 3,
  banner: 16 / 9,
  editorial: 3 / 2,
  billboard: 21 / 9,
};

export const ratioDeFormato = (formatoId: string | null): number =>
  (formatoId && RATIO_POR_FORMATO[formatoId]) || 1;

/**
 * El mismo formato, como lo pide el backend: un RATIO (`4:5`), no el id del picker. Es
 * la inversa de `ASPECTO_A_SLUG` de `verificacion/configurarRegeneracion.ts`; un id
 * desconocido cae a 4:5, el default de toda la generación de imagen.
 */
const FORMATO_POR_ID: Record<string, FormatoVariantes> = {
  post: '4:5',
  story: '9:16',
  square: '1:1',
  portrait: '2:3',
  banner: '16:9',
  editorial: '3:2',
  billboard: '21:9',
};

export const formatoDeRatioId = (formatoId: string | null): FormatoVariantes =>
  (formatoId && FORMATO_POR_ID[formatoId]) || '4:5';

/**
 * La bajada de la card de Acabados: "4 elegidos · colores y maderas".
 *
 * Las categorías salen de lo elegido y en el orden de `CATEGORIAS_ACABADO`, no en el
 * orden en que se fue tocando: la card tiene que decir lo mismo cada vez que se mira.
 */
export const resumenAcabados = (acabados: Acabado[]): string => {
  if (acabados.length === 0) return 'Ninguno elegido todavía';
  const cats = CATEGORIAS_ACABADO.filter((c) =>
    acabados.some((a) => a.categoria === c.id),
  ).map((c) => c.nombreCorto);
  const lista =
    cats.length <= 1
      ? (cats[0] ?? '')
      : `${cats.slice(0, -1).join(', ')} y ${cats[cats.length - 1]}`;
  return `${acabados.length} elegido${acabados.length === 1 ? '' : 's'} · ${lista}`;
};
